import { Injectable } from '@angular/core';
import { BaseService } from '../../core';
// import AWS from 'aws-sdk/global';
// import S3 from 'aws-sdk/clients/s3';
import { HttpClient, HttpParams } from '@angular/common/http';
import { UploadOption, FileType, FileAccessType, ACL, MimeTypes } from './file-upload.type';
import { FileAttachmentDto, ATTACHMENT_REASON_TYPE } from './dto';
import { CurrencyPipe } from '@angular/common';
import { Store } from '@ngrx/store';
import * as IndicatorStore from '../../store/indicator';
import { mergeMap } from 'rxjs/operators';
import { of } from 'rxjs';

export enum UPLOAD_TYPE {
  COMPANY_ATTACHMENTS = 'company-attachments',
  EDITOR_ATTACHMENTS = 'editor-attachments',
  INQUIRY_ATTACHMENTS = 'inquiry-attachments',
  BUYER_ATTACHMENTS = 'buyer-attachments',
  BANNER = 'banner',
  BRAND_LOGO = 'brand-logo',
  BRAND_BANNER = 'brand-banner',
  PRODUCT_IMAGE = 'product-image',
  PRODUCT_MSDS = 'product-msds',
}

type UploadOptionConfig = {
  [key in UPLOAD_TYPE]: UploadOption;
};

@Injectable({
  providedIn: 'root',
})
export class FileUploadService extends BaseService {
  private static UPLOAD_OPTIONS: UploadOptionConfig = {
    [UPLOAD_TYPE.COMPANY_ATTACHMENTS]: {
      path: 'buyer-company',
      sizeLimit: 1024 * 1024 * 10,
      fileType: FileType.DOCUMENT,
      mimeType: FileType.DOCUMENT,
      accessType: FileAccessType.PRIVATE,
      acl: ACL.PUBLIC,
    },
    [UPLOAD_TYPE.BRAND_LOGO]: {
      path: 'img/brand-logo',
      sizeLimit: 1024 * 1024 * 30,
      fileType: FileType.IMAGE,
      mimeType: FileType.IMAGE,
      // imageSizeArray: [[200, null]], // 원본,W200 리사이즈 저장
      // resized: true,
      // squared: false,
      // cropped: false,
      accessType: FileAccessType.PUBLIC,
      acl: ACL.PUBLIC,
    },
    [UPLOAD_TYPE.BRAND_BANNER]: {
      path: 'img/brand-banner',
      sizeLimit: 1024 * 1024 * 1,
      fileType: FileType.IMAGE,
      mimeType: FileType.IMAGE,
      // resized: false,
      // squared: false,
      // cropped: false,
      accessType: FileAccessType.PUBLIC,
      acl: ACL.PUBLIC,
    },
    [UPLOAD_TYPE.EDITOR_ATTACHMENTS]: {
      path: 'editor-attachments',
      sizeLimit: 1024 * 1024 * 10,
      fileType: FileType.DOCUMENT,
      mimeType: FileType.DOCUMENT,
      accessType: FileAccessType.PUBLIC,
      acl: ACL.PUBLIC,
    },
    [UPLOAD_TYPE.INQUIRY_ATTACHMENTS]: {
      path: 'inquiry-attachments',
      sizeLimit: 1024 * 1024 * 10,
      fileType: FileType.DOCUMENT,
      mimeType: FileType.DOCUMENT,
      accessType: FileAccessType.PRIVATE,
      acl: ACL.PUBLIC,
    },
    [UPLOAD_TYPE.BUYER_ATTACHMENTS]: {
      path: 'buyer-attachments',
      sizeLimit: 1024 * 1024 * 10,
      fileType: FileType.DOCUMENT,
      mimeType: FileType.DOCUMENT,
      accessType: FileAccessType.PRIVATE,
      acl: ACL.PUBLIC,
    },
    [UPLOAD_TYPE.BANNER]: {
      path: 'img/banner',
      sizeLimit: 1024 * 1024 * 1,
      fileType: FileType.IMAGE,
      mimeType: FileType.IMAGE,
      // resized: false,
      // squared: false,
      // cropped: false,
      accessType: FileAccessType.PUBLIC,
      acl: ACL.PUBLIC,
    },
    [UPLOAD_TYPE.PRODUCT_IMAGE]: {
      path: 'img/product-img',
      sizeLimit: 1024 * 1024 * 30,
      fileType: FileType.IMAGE,
      mimeType: FileType.IMAGE,
      // imageSizeArray: [[500, 500], [200, 200]], // 원본,500,200 리사이즈 저장
      // resized: true,
      // squared: true,
      // cropped: false,
      accessType: FileAccessType.PUBLIC,
      acl: ACL.PUBLIC,
    },
    [UPLOAD_TYPE.PRODUCT_MSDS]: {
      path: 'product-msds',
      sizeLimit: 1024 * 1024 * 10,
      fileType: FileType.DOCUMENT,
      mimeType: FileType.IMAGE,
      accessType: FileAccessType.PRIVATE,
      acl: ACL.PUBLIC,
    },
  };

  private readonly mimeTypes: MimeTypes = {
    [FileType.IMAGE]: ['image/gif', 'image/png', 'image/jpeg', 'image/jpg'],
    [FileType.DOCUMENT]: [
      'image/gif',
      'image/png',
      'image/jpeg',
      'image/jpg',
      'image/webp',
      'image/heic',
      'image/svg',
      'application/octet-stream',
      'application/vnd.ms-excel', // .xls
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-powerpoint', // .ppt
      'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
      'application/msword', // .doc
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
      'application/pdf', // .pdf
      'application/zip', // .zip
      'text/csv', // .csv
      'text/plain', // .txt
    ],
  };

  constructor(private awsClient: HttpClient, private currencyPipe: CurrencyPipe, private store: Store<any>) {
    super();
  }

  async upload(uploadType: UPLOAD_TYPE, files: FileList) {
    const attachments: FileAttachmentDto[] = [];
    const uploadOption = FileUploadService.UPLOAD_OPTIONS[uploadType];

    this.store.dispatch(new IndicatorStore.ShowIndicator('file-upload'));

    // tslint:disable-next-line: prefer-for-of
    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      if (file.size > uploadOption.sizeLimit) {
        attachments.push(
          new FileAttachmentDto({
            attachmentReasonType: ATTACHMENT_REASON_TYPE.SIZE,
            originFilename: file.name,
            mimetype: file.type,
            fileSize: this.byte(file.size, 3),
          })
        );
        this.store.dispatch(new IndicatorStore.HideIndicator('file-upload'));
        return attachments;
      }

      if (!this.mimeTypes[uploadOption.mimeType].includes(file.type)) {
        attachments.push(
          new FileAttachmentDto({
            attachmentReasonType: ATTACHMENT_REASON_TYPE.CONTENT_TYPE,
            originFilename: file.name,
            mimetype: file.type,
            fileSize: this.byte(file.size, 3),
          })
        );
        this.store.dispatch(new IndicatorStore.HideIndicator('file-upload'));
        return attachments;
      }

      const response = await this.get<any>('/file-upload/retrieve-s3-presigned', {
        uploadType,
        filename: file.name,
        mimetype: file.type,
      }).toPromise();

      if (!response || !response.url) {
        attachments.push(
          new FileAttachmentDto({
            attachmentReasonType: ATTACHMENT_REASON_TYPE.ETC,
            originFilename: file.name,
            mimetype: file.type,
            fileSize: this.byte(file.size, 3),
          })
        );
        this.store.dispatch(new IndicatorStore.HideIndicator('file-upload'));
        return attachments;
      }

      await this.awsClient
        .put<any>(response.url, file, { headers: { 'Content-Type': file.type } })
        .toPromise();

      attachments.push(
        new FileAttachmentDto({
          attachmentReasonType: ATTACHMENT_REASON_TYPE.SUCCESS,
          originFilename: file.name,
          mimetype: file.type,
          source: response.source,
          key: response.key,
          fileSize: this.byte(file.size, 3),
          endpoint: '',
          uploadType,
        })
      );
    }

    this.store.dispatch(new IndicatorStore.HideIndicator('file-upload'));
    return attachments;
  }

  /**
   * byte 용량을 환산하여 반환
   * 용량의 크기에 따라 MB, KB, byte 단위로 환산함
   * @param fileSize  byte 값
   * @param fixed     환산된 용량의 소수점 자릿수
   * @returns str
   */
  byte(fileSize, fixed) {
    let str;

    // MB 단위 이상일때 MB 단위로 환산
    if (fileSize >= 1024 * 1024) {
      fileSize = fileSize / (1024 * 1024);
      fileSize = fixed === undefined ? fileSize : fileSize.toFixed(fixed);
      str = this.currencyPipe.transform(fileSize, '', '', '0.0') + ' MB';
    }
    // KB 단위 이상일때 KB 단위로 환산
    else if (fileSize >= 1024) {
      fileSize = fileSize / 1024;
      fileSize = fixed === undefined ? fileSize : fileSize.toFixed(fixed);
      str = this.currencyPipe.transform(fileSize, '', '', '0.0') + ' KB';
    }
    // KB 단위보다 작을때 byte 단위로 환산
    else {
      fileSize = fixed === undefined ? fileSize : fileSize.toFixed(fixed);
      str = this.currencyPipe.transform(fileSize, '', '', '0.0') + ' byte';
    }
    return str;
  }
}
